import { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Check, Palette, Sparkles, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { getThemes, getActiveTheme, setActiveTheme, saveCustomTheme } from '../../../services/api';
import { useTheme } from '../../../context/ThemeContext';

const SEASON_LABELS = {
  ordinary: 'Vanlig',
  liturgical: 'Liturgisk',
  seasonal: 'Säsong',
  special: 'Specialtid',
  custom: 'Anpassat',
};

const SeasonBadge = ({ season }) => (
  <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600">
    {SEASON_LABELS[season] || season}
  </span>
);

// All ten color slots — labelled, with hex value visible.
const COLOR_SLOTS = [
  { key: 'section1', label: 'Section 1' },
  { key: 'section2', label: 'Section 2' },
  { key: 'section3', label: 'Section 3' },
  { key: 'accent', label: 'Accent' },
  { key: 'accentSoft', label: 'Accent soft' },
  { key: 'accentText', label: 'Accent text' },
  { key: 'textHeading', label: 'Text heading' },
  { key: 'textBody', label: 'Text body' },
  { key: 'textMuted', label: 'Text muted' },
  { key: 'textHero', label: 'Text hero' },
];

const Swatch = ({ color, label }) => (
  <div className="flex items-center gap-2" title={`${label}: ${color}`}>
    <span
      className="w-6 h-6 rounded-md border border-gray-200 shadow-inner flex-shrink-0"
      style={{ background: color }}
    />
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] font-semibold text-gray-700">{label}</span>
      <span className="text-[9px] font-mono text-gray-500 uppercase">{color}</span>
    </div>
  </div>
);

const PREVIEW_BG_SLOTS = ['section1', 'section2', 'section3', 'accent', 'accentSoft', 'accentText'];

const TextSamplePreview = ({ colors }) => (
  <div className="border-t border-gray-100">
    {/* Section text sample */}
    <div className="px-3 py-2 text-xs space-y-0.5" style={{ background: colors.section1 }}>
      <div className="font-bold" style={{ color: colors.textHeading }}>Heading</div>
      <div style={{ color: colors.textBody }}>Body text</div>
      <div style={{ color: colors.textMuted }}>Muted helper</div>
    </div>
    {/* Hero text sample on dark background */}
    <div
      className="px-3 py-2 text-xs"
      style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #3a2828 100%)' }}
    >
      <div className="font-bold" style={{ color: colors.textHero || '#FFFFFF' }}>
        Hero heading
      </div>
    </div>
  </div>
);

const ThemeCard = ({ theme, isActive, isApplying, onActivate }) => (
  <Card
    className={`overflow-hidden transition-all card-hover shadow-xl ${
      isActive ? 'ring-2 ring-amber-500' : ''
    }`}
    data-testid={`theme-card-${theme.id}`}
  >
    {/* Color preview band — backgrounds + accents */}
    <div className="h-16 flex" data-testid={`theme-preview-${theme.id}`}>
      {PREVIEW_BG_SLOTS.map((key) => (
        <div
          key={key}
          className="flex-1"
          style={{ background: theme.colors[key] }}
          title={`${key}: ${theme.colors[key]}`}
        />
      ))}
    </div>
    {/* Text sample preview — shows how heading/body/muted render */}
    <TextSamplePreview colors={theme.colors} />
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="font-serif font-bold text-gray-900">{theme.name}</h3>
          <SeasonBadge season={theme.season} />
        </div>
        {isActive && (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full"
            data-testid={`theme-active-${theme.id}`}
          >
            <Check className="w-3 h-3" /> Aktivt
          </span>
        )}
      </div>
      {theme.description && (
        <p className="text-xs text-gray-600 mb-3">{theme.description}</p>
      )}

      {/* All nine swatches, labelled — 3-col grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {COLOR_SLOTS.map((slot) => (
          <Swatch key={slot.key} color={theme.colors[slot.key]} label={slot.label} />
        ))}
      </div>

      <Button
        size="sm"
        onClick={() => onActivate(theme.id)}
        disabled={isActive || isApplying}
        className={`w-full ${
          isActive
            ? 'bg-gray-100 text-gray-500 cursor-default hover:bg-gray-100'
            : 'bg-gray-900 hover:bg-gray-800 text-white'
        }`}
        data-testid={`theme-activate-${theme.id}`}
      >
        {isActive ? 'Aktivt' : isApplying ? 'Aktiverar…' : 'Aktivera'}
      </Button>
    </CardContent>
  </Card>
);

// Fallback colors used when no custom theme exists yet (mirrors the 'ordinary' preset).
const DEFAULT_CUSTOM_COLORS = {
  section1: '#FFFEF7',
  section2: '#FEF3C7',
  section3: '#FFFBEB',
  accent: '#D97706',
  accentSoft: '#FEF3C7',
  accentText: '#FFFFFF',
  textHeading: '#111827',
  textBody: '#374151',
  textMuted: '#6B7280',
  textHero: '#FFFBEB',
};

const CustomThemeBuilder = ({ existing, isActive, isApplying, onSaved, onActivate }) => {
  const [name, setName] = useState(existing?.name || 'Anpassat tema');
  const [colors, setColors] = useState(existing?.colors || DEFAULT_CUSTOM_COLORS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (existing) {
      setName(existing.name || 'Anpassat tema');
      setColors(existing.colors || DEFAULT_CUSTOM_COLORS);
      setDirty(false);
    }
  }, [existing]);

  const handleColorChange = (key, value) => {
    setColors((c) => ({ ...c, [key]: value }));
    setDirty(true);
  };

  const handleReset = () => {
    setColors(existing?.colors || DEFAULT_CUSTOM_COLORS);
    setName(existing?.name || 'Anpassat tema');
    setDirty(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveCustomTheme({
        name,
        description: 'Eget färgschema valt av admin',
        colors,
      });
      toast.success('Anpassat tema sparat');
      setDirty(false);
      onSaved(saved);
    } catch (e) {
      toast.error('Kunde inte spara anpassat tema');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className={`overflow-hidden transition-all card-hover shadow-xl border-2 border-dashed ${
        isActive ? 'border-amber-500 ring-2 ring-amber-500' : 'border-amber-300'
      }`}
      data-testid="theme-card-custom-builder"
    >
      {/* Live preview band — bg/accent slices */}
      <div className="h-16 flex" data-testid="theme-preview-custom">
        {PREVIEW_BG_SLOTS.map((key) => (
          <div
            key={key}
            className="flex-1"
            style={{ background: colors[key] }}
          />
        ))}
      </div>
      {/* Live text sample */}
      <TextSamplePreview colors={colors} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <div>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); setDirty(true); }}
                className="font-serif font-bold text-gray-900 h-8 px-2 text-base"
                data-testid="custom-theme-name-input"
              />
              <SeasonBadge season="custom" />
            </div>
          </div>
          {isActive && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
              <Check className="w-3 h-3" /> Aktivt
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 mb-4">
          Välj dina egna färger för varje sektion. Klicka på en färgruta för att öppna färgväljaren.
        </p>

        {/* Nine color pickers — 3-col grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          {COLOR_SLOTS.map((slot) => (
            <div key={slot.key} className="space-y-1">
              <Label htmlFor={`custom-${slot.key}`} className="text-[11px] font-semibold text-gray-700">
                {slot.label}
              </Label>
              <div className="flex items-center gap-2">
                <input
                  id={`custom-${slot.key}`}
                  type="color"
                  value={colors[slot.key]}
                  onChange={(e) => handleColorChange(slot.key, e.target.value)}
                  className="w-8 h-8 rounded-md border border-gray-200 cursor-pointer flex-shrink-0"
                  data-testid={`custom-color-${slot.key}`}
                />
                <Input
                  type="text"
                  value={colors[slot.key]}
                  onChange={(e) => handleColorChange(slot.key, e.target.value)}
                  className="h-8 px-2 text-xs font-mono uppercase"
                  pattern="^#([A-Fa-f0-9]{6})$"
                  data-testid={`custom-color-text-${slot.key}`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Save + activate actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleReset}
            disabled={!dirty || saving}
            variant="outline"
            data-testid="custom-theme-reset"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Återställ
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="custom-theme-save"
          >
            <Save className="w-3 h-3 mr-1" />
            {saving ? 'Sparar…' : 'Spara'}
          </Button>
          <Button
            size="sm"
            onClick={() => onActivate('custom')}
            disabled={isActive || isApplying || dirty || !existing}
            className={`flex-1 ${
              isActive
                ? 'bg-gray-100 text-gray-500 cursor-default hover:bg-gray-100'
                : 'bg-gray-900 hover:bg-gray-800 text-white'
            }`}
            data-testid="custom-theme-activate"
          >
            {isActive ? 'Aktivt' : isApplying ? 'Aktiverar…' : 'Aktivera'}
          </Button>
        </div>
        {dirty && (
          <p className="text-[10px] text-amber-700 mt-2">Spara först innan du kan aktivera.</p>
        )}
      </CardContent>
    </Card>
  );
};

const ThemeManager = () => {
  const [themes, setThemes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyingId, setApplyingId] = useState(null);
  const { reloadTheme } = useTheme();

  const load = async () => {
    try {
      const [list, active] = await Promise.all([getThemes(), getActiveTheme()]);
      setThemes(list || []);
      setActiveId(active?.id || null);
    } catch (err) {
      toast.error('Kunde inte ladda teman');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleActivate = async (themeId) => {
    setApplyingId(themeId);
    try {
      await setActiveTheme(themeId);
      setActiveId(themeId);
      await reloadTheme();
      toast.success('Tema aktiverat');
    } catch (err) {
      toast.error('Kunde inte aktivera tema');
    } finally {
      setApplyingId(null);
    }
  };

  const handleCustomSaved = (savedTheme) => {
    setThemes((prev) => {
      const others = prev.filter((t) => t.id !== 'custom');
      return [...others, savedTheme];
    });
    // If currently active is custom, reflect new colors immediately
    if (activeId === 'custom') {
      reloadTheme();
    }
  };

  const presetThemes = themes.filter((t) => t.id !== 'custom');
  const customTheme = themes.find((t) => t.id === 'custom') || null;

  return (
    <div data-testid="theme-manager">
      <div className="flex items-center gap-3 mb-6">
        <Palette className="w-7 h-7 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tema</h1>
          <p className="text-sm text-gray-600">
            Välj färgtema för hela webbplatsen. Tema appliceras direkt på publika sidor.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500" data-testid="theme-loading">
          Laddar teman…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {presetThemes.map((theme) => (
              <ThemeCard
                key={theme.id}
                theme={theme}
                isActive={theme.id === activeId}
                isApplying={applyingId === theme.id}
                onActivate={handleActivate}
              />
            ))}
          </div>

          {/* Custom theme builder */}
          <div className="mt-2">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Anpassat tema
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CustomThemeBuilder
                existing={customTheme}
                isActive={activeId === 'custom'}
                isApplying={applyingId === 'custom'}
                onSaved={handleCustomSaved}
                onActivate={handleActivate}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeManager;
